import { WaterbodyDetail } from '@omniafishing/core';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import {
  XWeatherConditions,
  xWeatherConditionsAtTimestamp,
  XWeatherSunMoon,
  xWeatherSunMoon,
} from '../../../lib/xweather';
import { SEO } from '../../seo/seo';

interface WaterbodyDetailCurrentConditionsSEOProps {
  waterbodyDetail: WaterbodyDetail;
}

export const WaterbodyDetailCurrentConditionsSEO = React.memo(
  (props: WaterbodyDetailCurrentConditionsSEOProps) => {
    const { waterbodyDetail } = props;

    const { primary_name, lat, lng, water_temperature, water_temperature_recorded_at } =
      waterbodyDetail;
    const [solunarData, setSolunarData] = useState<XWeatherSunMoon>();
    const [weatherData, setWeatherData] = useState<XWeatherConditions>();

    useEffect(() => {
      const timestamp = new Date().toISOString();
      xWeatherConditionsAtTimestamp({
        lat,
        lng,
        timestamp,
      }).then((res) => {
        setWeatherData(res.data.response[0].periods[0]);
      });

      xWeatherSunMoon({
        lat,
        lng,
        timestamp,
      }).then((res) => {
        setSolunarData(res.data.response[0]);
      });
    }, [lat, lng]);

    const isoDate = dayjs(water_temperature_recorded_at).format();

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name: `${primary_name} Water Temperature`,
          description: `Current water temperature for ${primary_name}. Up-to-date accurate temperature readings to help plan your fishing activities.`,
          dateModified: isoDate,

          mainEntity: {
            '@type': 'HydrologicalFeature',
            name: primary_name,
            geo: {
              '@type': 'GeoCoordinates',
              latitude: lat,
              longitude: lng,
            },
            temperature: {
              '@type': 'QuantitativeValue',
              value: water_temperature,
              unitCode: 'FAH',
            },
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: `What is the current water temperature of ${primary_name}?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `The latest recorded temperature is ${water_temperature}º Fahrenheit.`,
              },
            },
            {
              '@type': 'Question',
              name: `How often is the ${primary_name} water temperature updated?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `Water temperature data for ${primary_name} is updated regularly. The last update was made on ${dayjs(
                  water_temperature_recorded_at
                ).format(
                  'MMMM D, YYYY'
                )}. We ensure that the most recent and accurate temperature readings are available to you, with updates occurring throughout the week.`,
              },
            },
          ],
        },
        {
          '@type': 'WeatherObservation',
          name: `${primary_name} Water Temperature`,
          dateObserved: isoDate,
          temperature: {
            '@type': 'QuantitativeValue',
            value: weatherData?.tempF,
            unitCode: 'FAH',
          },
          wind: {
            '@type': 'Wind',
            speed: {
              '@type': 'QuantitativeValue',
              value: weatherData?.windSpeedMPH,
              unitCode: 'MPH',
            },
            direction: {
              '@type': 'Direction',
              name: weatherData?.windDirDEG,
            },
          },
          precipitation: {
            '@type': 'QuantitativeValue',
            unitCode: 'QuantitativeValue',
            value: Number(weatherData?.pop),
          },
          pressure: {
            '@type': 'Pressure',
            value: weatherData?.spressureIN,
            unitCode: 'IN',
          },
          additionalProperty: {
            '@type': 'PropertyValue',
            name: 'Water Temperature',
            value: {
              '@type': 'QuantitativeValue',
              value: water_temperature,
              unitCode: 'FAH',
            },
          },
          location: {
            '@type': 'Place',
            name: primary_name,
            geo: {
              '@type': 'GeoCoordinates',
              latitude: lat,
              longitude: lng,
            },
          },
        },
        {
          '@type': 'WeatherForecast',
          name: `${primary_name} Weather Conditions`,
          dateIssued: new Date().toISOString(),
          weather: {
            '@type': 'Weather',
            name: weatherData?.weatherPrimary,
            description: weatherData?.weatherPrimary,
          },
          additionalProperty: {
            '@type': 'PropertyValue',
            name: 'Water Temperature',
            value: {
              '@type': 'QuantitativeValue',
              value: water_temperature,
              unitCode: 'FAH',
            },
          },
          temperature: {
            '@type': 'QuantitativeValue',
            value: weatherData?.tempF,
            unitCode: 'FAH',
          },
          wind: {
            '@type': 'Wind',
            speed: {
              '@type': 'QuantitativeValue',
              value: weatherData?.windSpeedMPH,
              unitCode: 'MPH',
            },
            direction: {
              '@type': 'Direction',
              name: weatherData?.windDirDEG,
            },
          },
          precipitation: {
            '@type': 'QuantitativeValue',
            unitCode: 'QuantitativeValue',
            value: Number(weatherData?.pop),
          },
          pressure: {
            '@type': 'Pressure',
            value: weatherData?.spressureIN,
            unitCode: 'IN',
          },
          solunar: {
            moonPhase: solunarData?.moon?.phase.name,
            moonrise: solunarData?.moon?.riseISO,
            moonset: solunarData?.moon?.setISO,
            sunrise: solunarData?.sun?.riseISO,
            sunset: solunarData?.sun?.setISO,
          },
          location: {
            '@type': 'Place',
            name: primary_name,
            geo: {
              '@type': 'GeoCoordinates',
              latitude: lat,
              longitude: lng,
            },
          },
        },
      ],
    };

    return (
      <SEO
        title={`${primary_name} Water Temperature`}
        description={`Track the current water temperature for ${primary_name}.`}
        meta={[{ name: 'date', content: isoDate }]}
        schema={schema}
      />
    );
  }
);
